import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { client } from '../lib/api'
import { Logo } from '../lib/ui'
import { isNativeApp } from '../lib/nativeAuth'

function authPageClass() {
  return `auth-page${isNativeApp() ? ' auth-page--native' : ''}`
}

function hashQueryParam(name: string): string | null {
  const hash = window.location.hash
  const q = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
  return new URLSearchParams(q).get(name)
}

export function ForgotPasswordPage({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setMsg(null)
    setBusy(true)
    try {
      const res = await client.requestPasswordReset(email)
      setMsg(res.message)
      if (res.devResetToken) {
        setMsg(`${res.message} Dev token: ${res.devResetToken}`)
      }
    } catch (error) {
      setErr((error as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={authPageClass()}>
      <div className="auth-card">
        <Logo compact />
        <button type="button" className="text-button auth-back" onClick={onDone}>
          <ArrowLeft size={16} /> Back to sign in
        </button>
        <h1>Reset password</h1>
        <p className="page-sub">Enter your email and we&apos;ll send a reset link.</p>
        <form className="auth-form" onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label htmlFor="reset-email">Email</label>
            <input
              id="reset-email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {err && <p className="field-error">{err}</p>}
          {msg && <p className="field-hint">{msg}</p>}
          <button className="button primary" type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      </div>
    </div>
  )
}

export function ResetPasswordPage({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const t = hashQueryParam('token')
    if (t) setToken(t)
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      const res = await client.resetPassword(token, password)
      setMsg(res.message ?? 'Password updated.')
      window.setTimeout(onDone, 1500)
    } catch (error) {
      setErr((error as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={authPageClass()}>
      <div className="auth-card">
        <Logo compact />
        <h1>Choose a new password</h1>
        <form className="auth-form" onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {err && <p className="field-error">{err}</p>}
          {msg && <p className="field-hint">{msg}</p>}
          <button className="button primary" type="submit" disabled={busy || !token}>
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  )
}

export function VerifyEmailPage({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const t = hashQueryParam('token')
    if (t) {
      setToken(t)
      void (async () => {
        setBusy(true)
        try {
          await client.verifyEmail(t)
          setMsg('Email verified — you can sign in.')
          window.setTimeout(onDone, 1500)
        } catch (error) {
          setErr((error as Error).message)
        } finally {
          setBusy(false)
        }
      })()
    }
  }, [onDone])

  return (
    <div className={authPageClass()}>
      <div className="auth-card">
        <Logo compact />
        <h1>Verify email</h1>
        {busy && !err && !msg && <p className="page-sub">Verifying…</p>}
        {err && <p className="field-error">{err}</p>}
        {msg && <p className="field-hint">{msg}</p>}
        {!token && !busy && <p className="field-error">Missing verification link.</p>}
      </div>
    </div>
  )
}
